import React, { useEffect, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import RecommenderFooter from "../../components/Footer/RecommenderFooter";
import JoyRide from "../../components/JoyRide/Joyride";
import GradePopup from "./GradePopup";
import { trackMentorRecommendationRouteVisit } from "../../stores/recommendations/mentorRecommendationsSlice";
import { useAppDispatch, useAppSelector } from "../../app/store";
import type {
  RecommenderPageSortSelections,
  RecommenderOutletContext,
  RecommenderPageFilters,
} from "./recommenderPageTypes";
import styles from "./RecommenderPage.module.css";
import Header from "../../components/Header/Header";
import Sidebar from "../../components/Sidebar/Sidebar";

const MainContent = () => {
  const [pageFilters, setPageFilters] = useState<RecommenderPageFilters>({
    exploreCareers: { subfields: [] },
    findGraduateSchools: { subfields: [], degrees: [] },
    findInternships: { subfields: [] },
  });
  const [sortSelections, setSortSelections] =
    useState<RecommenderPageSortSelections>({
      exploreCareers: "featured",
      findGraduateSchools: "featured",
      findInternships: "featured",
    });

  const outletContext: RecommenderOutletContext = {
    pageFilters,
    setPageFilters,
    sortSelections,
    setSortSelections,
  };

  return <Outlet context={outletContext} />;
};

const DismissibleGradePopup = ({
  submittingGrade,
}: {
  submittingGrade: boolean;
}) => {
  const [open, setOpen] = useState(
    localStorage.getItem("hasDismissedGradePopup") !== "true",
  );

  if (!open) return null;

  return (
    <GradePopup
      onDismiss={() => {
        localStorage.setItem("hasDismissedGradePopup", "true");
        setOpen(false);
      }}
      submittingGrade={submittingGrade}
    />
  );
};

const RecommenderPage = ({
  gradePosted,
  submittingGrade,
  completedPostSurvey,
  isResearchParticipant,
  isPilotParticipant,
}: {
  gradePosted: boolean;
  submittingGrade: boolean;
  completedPostSurvey: boolean;
  isResearchParticipant: boolean;
  isPilotParticipant: boolean;
}): React.JSX.Element => {
  const recommendations = useAppSelector(
    (state) => state.mentorRecommendations.data,
  );
  const dispatch = useAppDispatch();
  const location = useLocation();

  const tourRecommendations = {
    recommendedPages: [
      {
        pageRoute: "/find-graduate-schools",
        seen: false,
        priority: 1,
      },
      {
        pageRoute: "/explore-careers",
        seen: false,
        priority: 2,
      },
      {
        pageRoute: "/get-research-experience",
        seen: false,
        priority: 3,
      },
    ],
    recommendedFilters: {
      subFields: ["Clinical Psychology"],
      degrees: ["Masters"],
      adjusted: false,
    },
  };

  const [runTour, setRunTour] = useState(
    localStorage.getItem("hasVisitedRecommender") !== "true",
  );

  const popupPhase = submittingGrade
    ? "submitting"
    : gradePosted
      ? "posted"
      : null;

  useEffect(() => {
    if (!recommendations) return;

    void dispatch(trackMentorRecommendationRouteVisit(location.pathname));
  }, [dispatch, location.pathname, recommendations]);

  return (
    <>
      <JoyRide runTour={runTour} setRunTour={setRunTour} />
      {popupPhase && (
        <DismissibleGradePopup
          key={popupPhase}
          submittingGrade={submittingGrade}
        />
      )}

      <div className={styles.wrapper}>
        <Header />
        <main className={styles.recommenderMain}>
          <div className={styles.recommenderLayout}>
            <Sidebar
              tempRecommendations={runTour ? tourRecommendations : undefined}
            />
            <div style={{ minWidth: 0, width: "100%" }}>
              <MainContent />
            </div>
          </div>
        </main>
      </div>

      <RecommenderFooter
        enableTimer={!completedPostSurvey && isResearchParticipant}
        isPilotParticipant={isPilotParticipant}
      />
    </>
  );
};

export default RecommenderPage;
